import React, { useEffect, useState } from 'react';
import { useChatStore } from '@store/chat';
import ChatPopupWrapper from '../ChatPopupWrapper';
import { FormProvider } from 'react-hook-form';
import { ScheduledMessageFormKey } from '@asset/enum/chatEnum';
import { useMutation, useQuery } from 'react-query';
import { uploadChatFiles } from '@asset/function/chat';
import { toast } from 'react-toastify';
import { patchScheduledMessage, postScheduledMessage } from '@apis/api/chat';
import { DateTime } from 'luxon';
import { urlToFileObject } from '@asset/function/commonFunctions';
import _ from 'lodash';
import { Button } from '@components/amc/Button';
import { DateTimePicker } from '@components/common/DateTimePicker';
import { getStudentList } from '@apis/api/dashboard';
import * as S from '@styles/chat/scheduleMessage.style';
import StudentSelector from './StudentSelector';
import { useUserInfoQuery } from '@apis/query/user';
import AttachImageFormV2 from '@components/common/form/AttachImageFormV2';
import { StudentStatus } from '@asset/enum/UserEnum';
import { cn } from '@/lib/utils';
import { useTranslation } from 'next-i18next';
import { IconCalendarWeek, IconX } from '@tabler/icons-react';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const ScheduledMessageFormPopup = ({
  popupRef,
  selectedMessage,
  defaultPosition,
  scheduledMessageForm,
  isTablet,
  files,
  onDrag,
  togglePopupVisible,
  openScheduledMessageListPopup,
  onSuccessRegisterScheduledMessage,
  onDeleteScheduledMessage,
  setFiles
}) => {
  const {
    t,
    ready
  } = useTranslation('modal');
  const {
    data: user
  } = useUserInfoQuery();
  const {
    selectedChannel
  } = useChatStore();
  const selectedChannelInfo = selectedMessage ? {
    url: selectedMessage.channelUrl,
    studentName: selectedMessage.studentName
  } : selectedChannel;
  const [hasChange, setHasChange] = useState(false);
  const resetForm = () => {
    scheduledMessageForm.setValue(ScheduledMessageFormKey.RECIPIENT, {
      name: selectedChannelInfo.studentName,
      channelUrl: selectedChannelInfo.url
    });
    scheduledMessageForm.setValue(ScheduledMessageFormKey.MESSAGE, '');
    setFiles([]);
    scheduledMessageForm.setValue(ScheduledMessageFormKey.SCHEDULED_AT, null);
  };
  const {
    data: students
  } = useQuery(['GET_STUDENT_LIST'], async () => {
    const response = await getStudentList({
      pageSize: 10000,
      status: StudentStatus.ENROLLED,
      teacherId: user?.id
    });
    if (response.success) {
      return _.filter(response.students, student => !_.isNil(student.sendbirdChannelUrl));
    }
    return [];
  }, {
    initialData: [],
    enabled: !!user?.id
  });
  const syncFilesToRemoteStorage = async () => {
    const remoteFiles = files.filter(file => file.isRemote);
    const localFiles = await Promise.all(files.filter(file => !file.isRemote).map(file => urlToFileObject(file.url)));
    const newRemoteFiles = (await uploadChatFiles(localFiles, () => toast.error('일부 사진 업로드에 실패했습니다.'))).map(file => ({
      url: file.url,
      isRemote: true
    }));
    return [...remoteFiles, ...newRemoteFiles];
  };
  const {
    isLoading: isLoadingRegisterScheduledMessage,
    mutateAsync: registerScheduledMessage
  } = useMutation(['REGISTER_SCHEDULED_MESSAGE', selectedChannelInfo?.url], async formResult => {
    if (!_.isNil(selectedChannelInfo?.url)) {
      const fileUrls = (await syncFilesToRemoteStorage()).map(file => ({
        url: file.url
      }));
      const postScheduledMessageResponse = await postScheduledMessage(formResult[ScheduledMessageFormKey.RECIPIENT].channelUrl, {
        ...formResult,
        files: fileUrls
      });
      if (postScheduledMessageResponse.success) {
        resetForm();
        onSuccessRegisterScheduledMessage();
      }
    }
  });
  const {
    isLoading: isLoadingUpdateScheduledMessage,
    mutateAsync: updateScheduledMessage
  } = useMutation(['UPDATE_SCHEDULED_MESSAGE'], async formResult => {
    const _requestBody = {};
    const {
      isRecipientDifferent,
      isMessageDifferent,
      areFilesDifferent,
      isScheduledAtDifferent
    } = await checkDifferenceWithMessage(selectedMessage);
    if (isRecipientDifferent) {
      _requestBody['channelUrl'] = formResult[ScheduledMessageFormKey.RECIPIENT].channelUrl;
    }
    if (isMessageDifferent) {
      _requestBody[ScheduledMessageFormKey.MESSAGE] = formResult[ScheduledMessageFormKey.MESSAGE];
    }
    if (areFilesDifferent) {
      _requestBody['files'] = (await syncFilesToRemoteStorage()).map(file => ({
        url: file.url
      }));
    }
    if (isScheduledAtDifferent) {
      _requestBody[ScheduledMessageFormKey.SCHEDULED_AT] = formResult[ScheduledMessageFormKey.SCHEDULED_AT];
    }
    const patchScheduledMessageResponse = await patchScheduledMessage(selectedMessage.id, _requestBody);
    if (patchScheduledMessageResponse.success) {
      resetForm();
      onSuccessRegisterScheduledMessage();
    }
  });
  const isFormValid = !isLoadingRegisterScheduledMessage && !isLoadingUpdateScheduledMessage && scheduledMessageForm.formState.isValid && !_.isNil(scheduledMessageForm.watch(ScheduledMessageFormKey.SCHEDULED_AT));
  const setupMessageFormData = async message => {
    scheduledMessageForm.setValue(ScheduledMessageFormKey.RECIPIENT, {
      name: message.studentName,
      channelUrl: message.channelUrl
    });
    scheduledMessageForm.setValue(ScheduledMessageFormKey.MESSAGE, message.message);
    scheduledMessageForm.setValue(ScheduledMessageFormKey.SCHEDULED_AT, DateTime.fromISO(message.scheduledAt));
    setFiles(message.files.map(file => ({
      url: file.url,
      isRemote: true
    })));
  };
  useEffect(() => {
    if (!_.isNil(selectedMessage)) {
      setupMessageFormData(selectedMessage);
    } else {
      resetForm();
    }
  }, [selectedMessage]);
  const checkDifferenceWithMessage = async targetMessage => {
    const formValues = scheduledMessageForm.getValues();
    const isRecipientDifferent = targetMessage.channelUrl !== formValues[ScheduledMessageFormKey.RECIPIENT]?.channelUrl;
    const isMessageDifferent = targetMessage.message !== formValues[ScheduledMessageFormKey.MESSAGE];
    const localFileAdded = files.some(file => !file.isRemote);
    const remoteFileDeleted = targetMessage.files.length !== files.filter(file => file.isRemote).length;
    const areFilesDifferent = localFileAdded || remoteFileDeleted;
    const isScheduledAtDifferent = !DateTime.fromISO(targetMessage.scheduledAt).equals(formValues[ScheduledMessageFormKey.SCHEDULED_AT]);
    return {
      isRecipientDifferent,
      isMessageDifferent,
      areFilesDifferent,
      isScheduledAtDifferent
    };
  };
  useEffect(() => {
    const checkDifference = async () => {
      if (!_.isNil(selectedMessage)) {
        const {
          isRecipientDifferent,
          isMessageDifferent,
          isScheduledAtDifferent,
          areFilesDifferent
        } = await checkDifferenceWithMessage(selectedMessage);
        setHasChange(isRecipientDifferent || isMessageDifferent || isScheduledAtDifferent || areFilesDifferent);
      }
    };
    checkDifference();
  }, [scheduledMessageForm.watch()]);
  return /*#__PURE__*/_jsx(ChatPopupWrapper, {
    draggableCoreProps: {
      onDrag
    },
    popupRef: popupRef,
    defaultPosition: defaultPosition,
    rndProps: {
      enableResizing: {
        bottom: !isTablet
      }
    },
    children: /*#__PURE__*/_jsxs("section", {
      className: "flex flex-col h-full",
      children: [/*#__PURE__*/_jsxs(S.ScheduledMessagePopupHeader, {
        className: "drag-handle",
        children: [/*#__PURE__*/_jsx("div", {
          className: "middle title",
          children: "\uBA54\uC2DC\uC9C0 \uC608\uC57D"
        }), /*#__PURE__*/_jsx(Button, {
          variant: "Ghost",
          icon: /*#__PURE__*/_jsx(IconX, {}),
          size: "Medium",
          onClick: () => togglePopupVisible(false)
        })]
      }), /*#__PURE__*/_jsx(S.ScheduledMessageForm, {
        onSubmit: scheduledMessageForm.handleSubmit(async data => {
          if (_.isNil(selectedMessage)) {
            await registerScheduledMessage(data);
          } else {
            await updateScheduledMessage(data);
          }
        }),
        children: /*#__PURE__*/_jsxs(FormProvider, {
          ...scheduledMessageForm,
          children: [/*#__PURE__*/_jsxs("div", {
            className: "wrapper",
            children: [/*#__PURE__*/_jsx(StudentSelector, {
              scheduledMessageForm: scheduledMessageForm,
              students: students
            }), /*#__PURE__*/_jsx(S.MessageTextArea, {
              children: /*#__PURE__*/_jsx("textarea", {
                ...scheduledMessageForm.register(ScheduledMessageFormKey.MESSAGE, {
                  required: true
                }),
                placeholder: "\uC608\uC57D \uBA54\uC2DC\uC9C0\uB97C \uC785\uB825\uD558\uC138\uC694."
              })
            }), /*#__PURE__*/_jsxs(S.AdvancedForm, {
              children: [/*#__PURE__*/_jsxs("div", {
                className: "attach-image",
                children: [/*#__PURE__*/_jsx("div", {
                  className: "title",
                  children: ready && t('modal:teachingNote.form.commentImg.name')
                }), /*#__PURE__*/_jsx(AttachImageFormV2, {
                  isEditMode: true,
                  files: files,
                  onAddFiles: addedFiles => {
                    setFiles(prev => [...prev, ...addedFiles]);
                  },
                  onDeleteFiles: deletedFiles => {
                    setFiles(prev => prev.filter(file => !deletedFiles.includes(file)));
                  }
                })]
              }), /*#__PURE__*/_jsxs("div", {
                className: "time",
                children: [/*#__PURE__*/_jsx("div", {
                  className: "title",
                  children: "\uC608\uC57D \uC2DC\uAC04"
                }), /*#__PURE__*/_jsx(DateTimePicker, {
                  disabled: {
                    before: DateTime.now()
                  },
                  defaultDateTime: selectedMessage ? DateTime.fromISO(selectedMessage[ScheduledMessageFormKey.SCHEDULED_AT]) : null,
                  onTimeChange: date => {
                    scheduledMessageForm.setValue(ScheduledMessageFormKey.SCHEDULED_AT, date, {
                      shouldDirty: true
                    });
                  },
                  renderInput: ({
                    isOpen,
                    date
                  }) => /*#__PURE__*/_jsxs("button", {
                    className: cn('flex items-center gap-2 rounded-lg w-full justify-start text-left font-normal border-solid text-my-gray-10 text-label-lg-500 border border-neutral-200 bg-white hover:bg-neutral-100 hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-800 dark:hover:text-neutral-50', !date && 'text-muted-foreground', isOpen ? 'border-[2px] border-my-blue-50 p-[calc(0.75rem-1px)]' : 'border border-my-gray-80 p-3'),
                    children: [/*#__PURE__*/_jsx(IconCalendarWeek, {
                      size: 16,
                      color: "var(--gray-50)"
                    }), date ? date.toFormat('yyyy.MM.dd ccc a hh:mm', {
                      locale: 'ko'
                    }) : /*#__PURE__*/_jsx("span", {
                      className: "text-my-gray-60 text-label-lg-500",
                      children: "\uB0A0\uC9DC \uBC0F \uC2DC\uAC04 \uC120\uD0DD"
                    })]
                  })
                })]
              })]
            })]
          }), /*#__PURE__*/_jsxs(S.ScheduledMessagePopupFooter, {
            children: [/*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(Button, {
                variant: "Outline",
                size: "Medium",
                text: '예약 목록',
                onClick: () => openScheduledMessageListPopup()
              })
            }), /*#__PURE__*/_jsx("div", {
              children: selectedMessage ? /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(Button, {
                  variant: "Destructive",
                  size: "Medium",
                  text: '삭제',
                  onClick: () => onDeleteScheduledMessage(selectedMessage)
                }), /*#__PURE__*/_jsx(Button, {
                  variant: "Default",
                  size: "Medium",
                  disabled: !(isFormValid && hasChange),
                  type: "submit",
                  text: "\uC800\uC7A5"
                })]
              }) : /*#__PURE__*/_jsx(Button, {
                variant: "Default",
                size: "Medium",
                disabled: !isFormValid,
                type: "submit",
                text: "\uC608\uC57D"
              })
            })]
          })]
        })
      })]
    })
  });
};
export default ScheduledMessageFormPopup;